import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';
import { toast } from 'react-toastify';

const BASE = '/app_settings';

export const getAppSettings = createAsyncThunk(
  'AppSettings/getAppSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(BASE);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAppSetting = createAsyncThunk(
  'AppSettings/updateAppSetting',
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${BASE}/${key}`, { value });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const resetAppSetting = createAsyncThunk(
  'AppSettings/resetAppSetting',
  async (key, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(`${BASE}/${key}/reset`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const seedFromEnv = createAsyncThunk(
  'AppSettings/seedFromEnv',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${BASE}/seed_from_env`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const batchUpdateAppSettings = createAsyncThunk(
  'AppSettings/batchUpdateAppSettings',
  async (settings, { rejectWithValue }) => {
    try {
      const response = await apiAxios.patch(`${BASE}/batch_update`, { settings });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const uploadLogo = createAsyncThunk(
  'AppSettings/uploadLogo',
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const response = await apiAxios.post(`${BASE}/logo/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const syncOplaCustomFields = createAsyncThunk(
  'AppSettings/syncOplaCustomFields',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${BASE}/sync_opla_custom_fields`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const syncOplaSalesProcess = createAsyncThunk(
  'AppSettings/syncOplaSalesProcess',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${BASE}/sync_opla_sales_process`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const syncOplaContractTypes = createAsyncThunk(
  'AppSettings/syncOplaContractTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${BASE}/sync_opla_contract_types`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const syncOplaTaskTypes = createAsyncThunk(
  'AppSettings/syncOplaTaskTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${BASE}/sync_opla_task_types`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const syncOplaRelatedPartyRoles = createAsyncThunk(
  'AppSettings/syncOplaRelatedPartyRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${BASE}/sync_opla_related_party_roles`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deleteLogo = createAsyncThunk(
  'AppSettings/deleteLogo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(`${BASE}/logo`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

const appSettingsSlice = createSlice({
  name: 'appSettings',
  initialState: {
    data:             [],
    groups:           {},
    sub_group_labels: {},
    loading:          false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAppSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAppSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.data             = action.payload.data             || [];
        state.groups           = action.payload.groups           || {};
        state.sub_group_labels = action.payload.sub_group_labels || {};
      })
      .addCase(getAppSettings.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || action.error.message);
      })
      .addCase(updateAppSetting.fulfilled, (state, action) => {
        const updated = action.payload.data;
        state.data = state.data.map((s) => (s.key === updated.key ? updated : s));
        toast.success('Setting saved');
      })
      .addCase(updateAppSetting.rejected, (state, action) => {
        toast.error(action.payload || action.error.message);
      })
      .addCase(resetAppSetting.fulfilled, (state, action) => {
        const updated = action.payload.data;
        state.data = state.data.map((s) => (s.key === updated.key ? updated : s));
        toast.success('Reset to .env default');
      })
      .addCase(resetAppSetting.rejected, (state, action) => {
        toast.error(action.payload || action.error.message);
      })
      .addCase(batchUpdateAppSettings.fulfilled, (state, action) => {
        const updated = action.payload.data || [];
        const map = Object.fromEntries(updated.map((s) => [s.key, s]));
        state.data = state.data.map((s) => map[s.key] || s);
        toast.success(`${updated.length} setting(s) saved`);
      })
      .addCase(batchUpdateAppSettings.rejected, (state, action) => {
        toast.error(action.payload || action.error.message);
      })
      .addCase(seedFromEnv.fulfilled, (state, action) => {
        toast.success(action.payload.message || 'Seeded from .env successfully');
      })
      .addCase(seedFromEnv.rejected, (state, action) => {
        toast.error(action.payload || action.error.message);
      })
      .addCase(syncOplaCustomFields.fulfilled, (state, action) => {
        const updated = action.payload.data;
        if (updated) state.data = state.data.map((s) => (s.key === updated.key ? updated : s));
        toast.success(action.payload.message || 'Custom fields synced');
      })
      .addCase(syncOplaCustomFields.rejected, (state, action) => {
        toast.error(action.payload || action.error.message);
      })
      .addCase(syncOplaSalesProcess.fulfilled, (state, action) => {
        const updated = action.payload.data;
        if (updated) state.data = state.data.map((s) => (s.key === updated.key ? updated : s));
        toast.success(action.payload.message || 'Sales process synced');
      })
      .addCase(syncOplaSalesProcess.rejected, (state, action) => {
        toast.error(action.payload || action.error.message);
      })
      .addCase(syncOplaContractTypes.fulfilled, (state, action) => {
        const updated = action.payload.data;
        if (updated) state.data = state.data.map((s) => (s.key === updated.key ? updated : s));
        toast.success(action.payload.message || 'Contract types synced');
      })
      .addCase(syncOplaContractTypes.rejected, (state, action) => {
        toast.error(action.payload || action.error.message);
      })
      .addCase(syncOplaTaskTypes.fulfilled, (state, action) => {
        const updated = action.payload.data;
        if (updated) state.data = state.data.map((s) => (s.key === updated.key ? updated : s));
        toast.success(action.payload.message || 'Task types synced');
      })
      .addCase(syncOplaTaskTypes.rejected, (state, action) => {
        toast.error(action.payload || action.error.message);
      })
      .addCase(uploadLogo.fulfilled, (state, action) => {
        const url = action.payload.url;
        const exists = state.data.some((s) => s.key === 'APP_LOGO');
        if (exists) {
          state.data = state.data.map((s) =>
            s.key === 'APP_LOGO' ? { ...s, logo_url: url, value: url } : s
          );
        } else {
          // First upload — APP_LOGO record didn't exist in the initial load
          state.data = [
            ...state.data,
            { key: 'APP_LOGO', logo_url: url, value: url, group: 'ui', data_type: 'attachment', is_editable: true, is_sensitive: false },
          ];
        }
        toast.success('Logo uploaded successfully');
      })
      .addCase(uploadLogo.rejected, (state, action) => {
        toast.error(action.payload || action.error.message);
      })
      .addCase(deleteLogo.fulfilled, (state) => {
        state.data = state.data.map((s) =>
          s.key === 'APP_LOGO' ? { ...s, logo_url: null, value: null } : s
        );
        toast.success('Logo removed');
      })
      .addCase(deleteLogo.rejected, (state, action) => {
        toast.error(action.payload || action.error.message);
      });
  },
});

export default appSettingsSlice.reducer;
